import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ethers } from 'ethers';
import {
  claimToken,
  createEntitlementChallenge,
  fetchMyTokenClaims,
  fetchPaymentProducts,
  fetchTokenMetadata,
  updateClaim,
  verifyEntitlementChallenge,
} from './tokenIntelligenceApi';

const ERC20_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
];

function getStoredClaimKey(address) {
  if (!address || typeof window === 'undefined' || !window.localStorage) return '';
  return String(window.localStorage.getItem(`e3d_claim_key:${String(address).toLowerCase()}`) || '');
}

function hasStoredClaimKey(address) {
  return !!getStoredClaimKey(address);
}

function setStoredClaimKey(address, apiKey) {
  if (!address || !apiKey || typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(`e3d_claim_key:${String(address).toLowerCase()}`, String(apiKey));
}

function buildSocials(values) {
  const out = {};
  for (const [key, value] of Object.entries(values || {})) {
    const normalized = String(value || '').trim();
    if (normalized) out[key] = normalized;
  }
  return out;
}

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 48px',
    maxWidth: 720,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: { padding: '14px 12px 32px' },
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#9ea4d8',
    fontSize: 13,
    textDecoration: 'none',
    marginBottom: 18,
    '&:hover': { color: '#cfd6ff' },
  },
  panel: {
    padding: '24px 26px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.16), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.12)',
    [theme.breakpoints.down('sm')]: { padding: '16px 16px' },
  },
  title: {
    fontSize: '1.6rem',
    fontWeight: 900,
    color: '#e5e7ff',
    marginBottom: 6,
    [theme.breakpoints.down('sm')]: { fontSize: '1.3rem' },
  },
  subtitle: {
    fontSize: 13,
    color: '#9ea4d8',
    marginBottom: 22,
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    marginBottom: 22,
  },
  sectionLabel: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  feeBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 14,
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.03)',
  },
  row: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  claimListItem: {
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    padding: 14,
  },
  claimListItemActive: {
    background: 'rgba(77,121,255,0.10)',
  },
  actions: {
    display: 'flex',
    gap: 10,
    marginTop: 8,
  },
  primaryButton: {
    background: '#4d79ff',
    color: '#fff',
    fontWeight: 700,
    '&:hover': { background: '#3d63d9' },
    '&.Mui-disabled': { background: 'rgba(77,121,255,0.25)', color: 'rgba(255,255,255,0.4)' },
  },
  secondaryButton: {
    color: '#cfd6ff',
    borderColor: 'rgba(255,255,255,0.24)',
    '&:hover': { borderColor: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.04)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', borderColor: 'rgba(255,255,255,0.12)' },
  },
  status: { color: '#76ffb8', fontSize: 13 },
  error: { color: '#ff8c8c', fontSize: 13 },
  bodyText: { color: '#cfd6ff' },
  mutedText: { color: '#9ea4d8' },
  textField: {
    '& .MuiOutlinedInput-input': { color: '#fff' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4d79ff' },
    '& .MuiInputLabel-root': { color: '#9ea4d8' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#cfd6ff' },
    '& .MuiFormHelperText-root': { color: '#8d93c9' },
    '& .MuiSelect-icon': { color: '#9ea4d8' },
  },
  menuPaper: {
    background: '#1f2026',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.12)',
  },
}));

export default function TokenClaimPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { address } = useParams();
  const [searchParams] = useSearchParams();
  const mode = ['claim', 'edit', 'manage'].includes(searchParams.get('mode')) ? searchParams.get('mode') : 'claim';

  const [metadata, setMetadata] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState('');

  const existingClaim = metadata && metadata.claim ? metadata.claim : null;
  const [wallet, setWallet] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [contact, setContact] = useState('');
  const [socialX, setSocialX] = useState('');
  const [socialTelegram, setSocialTelegram] = useState('');
  const [socialDiscord, setSocialDiscord] = useState('');
  const [socialGithub, setSocialGithub] = useState('');
  const [feeTxHash, setFeeTxHash] = useState('');
  const [paymentProducts, setPaymentProducts] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [proofMethod, setProofMethod] = useState('owner_call');
  const [myClaims, setMyClaims] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const effectiveApiKey = getStoredClaimKey(address);

  const claimProduct = useMemo(() => {
    const products = paymentProducts && Array.isArray(paymentProducts.products) ? paymentProducts.products : [];
    return products.find((product) => product && product.product === 'claim') || null;
  }, [paymentProducts]);

  const paymentMethods = claimProduct && Array.isArray(claimProduct.paymentMethods) ? claimProduct.paymentMethods : [];
  const selectedMethod = paymentMethods.find((method) => method.id === paymentMethodId) || paymentMethods[0] || null;

  useEffect(() => {
    if (!address) return undefined;
    let active = true;
    setMetaLoading(true);
    setMetaError('');
    fetchTokenMetadata(address)
      .then((result) => { if (active) setMetadata(result); })
      .catch((err) => { if (active) setMetaError(err && err.message ? err.message : 'Failed to load token metadata'); })
      .finally(() => { if (active) setMetaLoading(false); });
    return () => { active = false; };
  }, [address]);

  useEffect(() => {
    setWebsite(existingClaim && existingClaim.website ? existingClaim.website : (metadata && metadata.website ? metadata.website : ''));
    setDescription(existingClaim && existingClaim.description ? existingClaim.description : (metadata && metadata.description ? metadata.description : ''));
    setContact(existingClaim && existingClaim.contact ? existingClaim.contact : (metadata && metadata.contact ? metadata.contact : ''));
    setSocialX(existingClaim && existingClaim.socials && existingClaim.socials.x ? existingClaim.socials.x : '');
    setSocialTelegram(existingClaim && existingClaim.socials && existingClaim.socials.telegram ? existingClaim.socials.telegram : '');
    setSocialDiscord(existingClaim && existingClaim.socials && existingClaim.socials.discord ? existingClaim.socials.discord : '');
    setSocialGithub(existingClaim && existingClaim.socials && existingClaim.socials.github ? existingClaim.socials.github : '');
  }, [existingClaim, metadata]);

  useEffect(() => {
    if (mode === 'manage') return undefined;
    let active = true;
    fetchPaymentProducts()
      .then((result) => {
        if (!active) return;
        setPaymentProducts(result);
        const products = result && Array.isArray(result.products) ? result.products : [];
        const claim = products.find((product) => product && product.product === 'claim');
        const methods = claim && Array.isArray(claim.paymentMethods) ? claim.paymentMethods : [];
        if (methods[0]) setPaymentMethodId((current) => current || methods[0].id);
      })
      .catch((err) => {
        if (!active) return;
        setError(err && err.message ? err.message : 'Failed to load claim payment configuration');
      });
    return () => { active = false; };
  }, [mode]);

  const verifyWalletSession = async (selectedWallet, signer, purpose) => {
    const challenge = await createEntitlementChallenge({
      wallet: selectedWallet,
      chain: 'ethereum',
      purpose,
    });
    const signature = await signer.signMessage(challenge.message);
    return verifyEntitlementChallenge({
      challengeId: challenge.challengeId,
      wallet: selectedWallet,
      chain: 'ethereum',
      message: challenge.message,
      signature,
    });
  };

  const handleConnectWallet = async () => {
    setError('');
    setStatus('');
    try {
      if (!window.ethereum) throw new Error('MetaMask is not installed');
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const selected = Array.isArray(accounts) && accounts[0] ? ethers.utils.getAddress(String(accounts[0])) : '';
      if (!selected) throw new Error('No wallet account is available');
      setWallet(selected);
      setStatus(`Connected wallet ${selected}`);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to connect wallet');
    }
  };

  const handleLoadMyClaims = async () => {
    setError('');
    setStatus('');
    setBusy(true);
    try {
      if (!wallet) throw new Error('Connect a wallet first');
      if (!window.ethereum) throw new Error('MetaMask is not installed');
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signer = provider.getSigner();
      const verified = await verifyWalletSession(wallet, signer, 'token_claim_dashboard');
      const result = await fetchMyTokenClaims({
        wallet,
        chain: 'ethereum',
        sessionToken: verified && verified.sessionToken ? verified.sessionToken : '',
      });
      const claims = result && Array.isArray(result.claims) ? result.claims : [];
      setMyClaims(claims);
      setStatus(claims.length ? `Loaded ${claims.length} claimed token${claims.length === 1 ? '' : 's'}.` : 'No claimed tokens found for this wallet.');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load claimed tokens');
    } finally {
      setBusy(false);
    }
  };

  const handlePayFee = async () => {
    setError('');
    setStatus('');
    try {
      if (!selectedMethod) throw new Error('Claim payment method is unavailable');
      if (!window.ethereum) throw new Error('MetaMask is not installed');
      if (!wallet) throw new Error('Connect a wallet first');
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== Number(selectedMethod.chainId)) {
        throw new Error(`Switch MetaMask to ${selectedMethod.network || selectedMethod.chain}`);
      }
      const signer = provider.getSigner();
      const token = new ethers.Contract(selectedMethod.tokenAddress, ERC20_ABI, signer);
      const amount = ethers.utils.parseUnits(String(claimProduct && claimProduct.claimFeeE3D ? claimProduct.claimFeeE3D : '0'), 18);
      setBusy(true);
      setStatus(`Submitting ${claimProduct && claimProduct.claimFeeE3D ? claimProduct.claimFeeE3D : '0'} ${selectedMethod.token} transfer...`);
      const tx = await token.transfer(selectedMethod.treasuryAddress, amount);
      const receipt = await tx.wait();
      setFeeTxHash(receipt && receipt.transactionHash ? receipt.transactionHash : tx.hash);
      setStatus(`Fee payment confirmed: ${receipt && receipt.transactionHash ? receipt.transactionHash : tx.hash}`);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to pay claim fee');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setStatus('');
    setBusy(true);
    try {
      const socials = buildSocials({
        x: socialX,
        telegram: socialTelegram,
        discord: socialDiscord,
        github: socialGithub,
      });

      if (mode === 'edit') {
        if (!effectiveApiKey) throw new Error('No stored claim API key found for this token on this browser');
        await updateClaim(address, {
          chain: 'ethereum',
          website,
          description,
          contact,
          socials,
        }, effectiveApiKey);
        setStatus('Claim updated. Returning to token page...');
        setTimeout(() => navigate(`/token/${address}`), 900);
        return;
      }

      if (mode === 'manage') {
        await handleLoadMyClaims();
        return;
      }

      if (!wallet) throw new Error('Connect a wallet first');
      if (!feeTxHash) throw new Error('Pay the fee or paste the claim payment transaction hash');
      if (!window.ethereum) throw new Error('MetaMask is not installed');
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signer = provider.getSigner();
      const verified = await verifyWalletSession(wallet, signer, 'token_claim');
      const result = await claimToken(address, {
        wallet,
        chain: 'ethereum',
        feeTxHash,
        paymentMethod: selectedMethod ? selectedMethod.id : '',
        paymentChain: selectedMethod ? selectedMethod.chain : '',
        proofMethod,
        website,
        description,
        contact,
        socials,
        sessionToken: verified.sessionToken,
      });
      if (result && result.apiKey) setStoredClaimKey(address, result.apiKey);
      setStatus('Token claimed. Returning to token page...');
      setTimeout(() => navigate(`/token/${address}`), 900);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save claim');
    } finally {
      setBusy(false);
    }
  };

  const pageTitle = mode === 'edit' ? 'Edit Token Listing' : mode === 'manage' ? 'My Claimed Tokens' : 'Claim This Token';
  const pageSubtitle = mode === 'edit'
    ? 'Update the owner-authored fields attached to this token.'
    : mode === 'manage'
      ? 'Review claims for this wallet and check whether each token still has a stored browser edit key.'
      : 'Pay the claim fee, then prove control with an owner()/deployer check or a wallet signature.';

  return (
    <Box className={classes.root}>
      <RouterLink to={`/token/${address}`} className={classes.backLink}>
        ← Back to token
      </RouterLink>

      <Box className={classes.panel}>
        <Typography className={classes.title}>{pageTitle}</Typography>
        <Typography className={classes.subtitle}>
          {metaLoading ? 'Loading token…' : (metadata && (metadata.name || metadata.symbol)) ? `${metadata.name || ''}${metadata.symbol ? ` (${metadata.symbol})` : ''} · ${address}` : address}
        </Typography>
        <Typography className={classes.bodyText} style={{ marginBottom: 22, fontSize: 14 }}>{pageSubtitle}</Typography>

        {metaError ? <Typography className={classes.error} style={{ marginBottom: 16 }}>{metaError}</Typography> : null}

        {mode !== 'edit' ? (
          <Box className={classes.section}>
            <Box className={classes.row}>
              <Button className={classes.secondaryButton} variant="outlined" onClick={handleConnectWallet} disabled={busy}>
                {wallet ? 'Wallet Connected' : 'Connect Wallet'}
              </Button>
              {wallet ? <Typography className={classes.bodyText} style={{ fontSize: 13, fontFamily: 'monospace' }}>{wallet}</Typography> : null}
              {mode === 'manage' ? (
                <Button className={classes.secondaryButton} variant="outlined" onClick={handleLoadMyClaims} disabled={busy || !wallet}>
                  Load My Claims
                </Button>
              ) : null}
            </Box>

            {mode !== 'manage' && claimProduct ? (
              <Box className={classes.feeBox}>
                <Typography className={classes.sectionLabel}>Claim fee</Typography>
                <Typography className={classes.mutedText} style={{ fontSize: 13 }}>
                  {claimProduct.claimFeeE3D} {selectedMethod ? selectedMethod.token : 'E3D'} to {selectedMethod ? selectedMethod.treasuryAddress : 'configured treasury'}
                </Typography>
                {paymentMethods.length ? (
                  <TextField
                    select
                    className={classes.textField}
                    label="Payment Method"
                    value={paymentMethodId}
                    onChange={(event) => setPaymentMethodId(event.target.value)}
                    variant="outlined"
                    size="small"
                    SelectProps={{ MenuProps: { PaperProps: { className: classes.menuPaper } } }}
                  >
                    {paymentMethods.map((method) => (
                      <MenuItem key={method.id} value={method.id}>
                        {method.token} on {method.chain}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
                <Box>
                  <Button className={classes.secondaryButton} variant="outlined" onClick={handlePayFee} disabled={busy || !wallet || !selectedMethod}>
                    Pay Fee In Wallet
                  </Button>
                </Box>
              </Box>
            ) : null}

            {mode !== 'manage' ? (
              <>
                <TextField
                  select
                  className={classes.textField}
                  label="Proof Method"
                  value={proofMethod}
                  onChange={(event) => setProofMethod(event.target.value)}
                  fullWidth
                  variant="outlined"
                  helperText="Use owner() or deployer proof when your wallet matches the contract evidence. Signature remains available as a fallback."
                  SelectProps={{ MenuProps: { PaperProps: { className: classes.menuPaper } } }}
                >
                  <MenuItem value="owner_call">owner() proof</MenuItem>
                  <MenuItem value="deployer">Deployer proof</MenuItem>
                  <MenuItem value="signature">Signature-only proof</MenuItem>
                </TextField>
                <TextField
                  className={classes.textField}
                  label="Fee Transaction Hash"
                  value={feeTxHash}
                  onChange={(event) => setFeeTxHash(event.target.value)}
                  fullWidth
                  variant="outlined"
                  helperText="Paste a confirmed ERC-20 transfer tx hash if you paid outside this page."
                />
              </>
            ) : null}
          </Box>
        ) : null}

        {mode !== 'manage' ? (
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Listing details</Typography>
            <TextField className={classes.textField} label="Website" value={website} onChange={(event) => setWebsite(event.target.value)} fullWidth variant="outlined" />
            <TextField className={classes.textField} label="Contact" value={contact} onChange={(event) => setContact(event.target.value)} fullWidth variant="outlined" />
            <TextField className={classes.textField} label="Description" value={description} onChange={(event) => setDescription(event.target.value)} fullWidth multiline rows={4} variant="outlined" />
            <TextField className={classes.textField} label="X" value={socialX} onChange={(event) => setSocialX(event.target.value)} fullWidth variant="outlined" />
            <TextField className={classes.textField} label="Telegram" value={socialTelegram} onChange={(event) => setSocialTelegram(event.target.value)} fullWidth variant="outlined" />
            <TextField className={classes.textField} label="Discord" value={socialDiscord} onChange={(event) => setSocialDiscord(event.target.value)} fullWidth variant="outlined" />
            <TextField className={classes.textField} label="GitHub" value={socialGithub} onChange={(event) => setSocialGithub(event.target.value)} fullWidth variant="outlined" />
          </Box>
        ) : null}

        {mode === 'manage' ? (
          <Box className={classes.section}>
            {myClaims.map((claimItem) => (
              <Box
                key={`${claimItem.chain}:${claimItem.address}`}
                className={`${classes.claimListItem} ${claimItem.address && String(claimItem.address).toLowerCase() === String(address || '').toLowerCase() ? classes.claimListItemActive : ''}`}
              >
                <Typography className={classes.bodyText} style={{ fontFamily: 'monospace', fontSize: 13 }}>{claimItem.address}</Typography>
                <Typography className={classes.mutedText} style={{ fontSize: 13 }}>
                  {claimItem.claimed ? 'Active claim' : 'Inactive claim'} · {claimItem.proofMethod}
                  {claimItem.stale ? ' · owner() changed' : ''}
                </Typography>
                {claimItem.claimedAt ? (
                  <Typography className={classes.mutedText} style={{ fontSize: 13 }}>
                    Updated: {claimItem.claimedAt}
                  </Typography>
                ) : null}
                {claimItem.website ? (
                  <Typography className={classes.mutedText} style={{ fontSize: 13 }}>
                    {claimItem.website}
                  </Typography>
                ) : null}
                <Typography className={classes.mutedText} style={{ fontSize: 13 }}>
                  {hasStoredClaimKey(claimItem.address) ? 'Stored edit key available on this browser.' : 'No stored edit key found on this browser.'}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}

        {status ? <Typography className={classes.status} style={{ marginBottom: 10 }}>{status}</Typography> : null}
        {error ? <Typography className={classes.error} style={{ marginBottom: 10 }}>{error}</Typography> : null}

        <Box className={classes.actions}>
          <Button className={classes.secondaryButton} variant="outlined" onClick={() => navigate(`/token/${address}`)} disabled={busy}>
            Cancel
          </Button>
          <Button className={classes.primaryButton} variant="contained" onClick={handleSubmit} disabled={busy}>
            {mode === 'edit' ? 'Save Changes' : mode === 'manage' ? 'Refresh List' : 'Claim Token'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
